import type { ParsedNamespaceKey } from '../types/translation.types.js';
/**
 * Namespace parsing utilities for translation keys
 * Supports patterns like: 'common:welcome', 'user.profile.name', 'errors:validation.required'
 */


/**
 * Parse namespace from translation key
 * Supports: 'namespace:key' pattern
 * Future: Could support other patterns like 'namespace.key' or 'namespace/key'
 * 
 * @param key - Translation key (e.g., 'common:welcome' or 'welcome')
 * @returns Parsed namespace information
 */
export function parseNamespaceKey(key: string): ParsedNamespaceKey {
  // Simple namespace pattern: 'namespace:key'
  const colonIndex = key.indexOf(':');
  
  if (colonIndex > 0 && colonIndex < key.length - 1) {
    // Has explicit namespace
    const namespace = key.substring(0, colonIndex);
    const cleanKey = key.substring(colonIndex + 1);
    
    return {
      originalKey: key,
      namespace,
      cleanKey,
      hasExplicitNamespace: true
    };
  }
  
  // No namespace found
  return {
    originalKey: key,
    cleanKey: key,
    hasExplicitNamespace: false
  };
}

/**
 * Create a full namespaced key
 * 
 * @param key - Clean translation key
 * @param namespace - Namespace to prepend
 * @returns Namespaced key (e.g., 'common:welcome')
 */
export function createNamespacedKey(key: string, namespace?: string): string {
  if (!namespace) return key;
  return `${namespace}:${key}`;
}

/**
 * Validate namespace format
 * 
 * @param namespace - Namespace to validate
 * @returns Whether namespace is valid
 */
export function isValidNamespace(namespace: string): boolean {
  // Simple validation: alphanumeric, underscore, hyphen
  return /^[a-zA-Z0-9_-]+$/.test(namespace);
}